
import { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { DaySchedule } from './schedule/types';
import { useScheduleDataLoader } from './schedule/useScheduleDataLoader';
import { useScheduleProcessor } from './schedule/useScheduleProcessor';
import { useScheduleHelpers } from './schedule/useScheduleHelpers';

// Re-export types so other components can import them
export type { DaySchedule } from './schedule/types';

export const useScheduleVisualization = (psychologistId?: string) => {
  const [daySchedules, setDaySchedules] = useState<Map<string, DaySchedule>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [lastLoadedRange, setLastLoadedRange] = useState<string>('');

  const { loadScheduleData: loadRawData } = useScheduleDataLoader();
  const { processScheduleData } = useScheduleProcessor();
  
  // Memoize helpers to prevent recreation
  const { getScheduleForDate, getDayColor, getDayTooltip } = useScheduleHelpers(daySchedules);

  // Stable callback that doesn't change unless psychologistId changes
  const loadScheduleData = useCallback(async (startDate: Date, endDate: Date) => {
    if (!psychologistId) {
      console.error('❌ [useScheduleVisualization] ERRO: Sem psychologistId');
      return;
    }

    const rangeKey = `${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}`;
    
    // Prevent loading same range multiple times
    if (rangeKey === lastLoadedRange) {
      console.log('♻️ [useScheduleVisualization] Range já carregado, usando cache');
      return;
    }

    setIsLoading(true);
    try {
      console.log('📅 [useScheduleVisualization] Carregando dados para:', rangeKey);
      
      const rawData = await loadRawData(psychologistId, startDate, endDate);
      const scheduleMap = processScheduleData(rawData, startDate, endDate);

      // Only update if we have new data
      setDaySchedules(scheduleMap);
      setLastLoadedRange(rangeKey);
      
      console.log('✅ [useScheduleVisualization] Dados carregados com sucesso');
    } catch (error) {
      console.error('🔥 [useScheduleVisualization] ERRO:', error);
    } finally {
      setIsLoading(false);
    }
  }, [psychologistId, lastLoadedRange, loadRawData, processScheduleData]);

  // Auto-load current month only when psychologistId changes
  useEffect(() => {
    if (psychologistId && psychologistId !== '') {
      console.log('🔄 [useScheduleVisualization] Auto-loading para psychologistId:', psychologistId);
      
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      loadScheduleData(startOfMonth, endOfMonth);
    }
  }, [psychologistId]); // Only depend on psychologistId, not loadScheduleData

  // Return stable memoized object
  return useMemo(() => ({
    daySchedules,
    isLoading,
    loadScheduleData,
    getScheduleForDate,
    getDayColor,
    getDayTooltip
  }), [daySchedules, isLoading, loadScheduleData, getScheduleForDate, getDayColor, getDayTooltip]);
};
